import './ContentEditableAreaForContextAttributes.scss';

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { FlightButton, FlightTooltip, getIcon } from '@flybits/design-system';
import AttributeMappingModal from 'components/FlowBuilder/FlowSidePanels/shared/AttributeMappingModal/AttributeMappingModal';
import { TAttributeParameter } from 'components/FlowBuilder/FlowSidePanels/types';
import usePlugins, { TContextAttribute, TContextPlugin } from 'hooks/useContextPlugins';
import { splitCtxString } from 'helpers/rule.helper';
import { CTX_ATTR_STRING_PATTERN } from 'constants/contextPlugins';

interface ContentEditableAreaForContextAttributesProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hasError?: boolean;
  errorMessage?: string;
  width?: string;
  maxLength?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  hideCharCounter?: boolean;
  className?: string;
}

const INCOMING_CONTEXT_PREFIX = 'incomingContext';
const ATTR_TOKEN_SOURCE = `(?:${INCOMING_CONTEXT_PREFIX})?\\{\\{${CTX_ATTR_STRING_PATTERN}+}}`;
const buildAttrSplitRegex = () => new RegExp(`(${ATTR_TOKEN_SOURCE})`, 'g');
const buildAttrExactRegex = () => new RegExp(`^${ATTR_TOKEN_SOURCE}$`);

/**
 * Splits a full attribute token into its inner ctx string (without the `incomingContext` prefix
 * or surrounding braces) and whether it uses the incoming/transient context prefix.
 */
const parseAttrToken = (token: string) => {
  const useIncomingContext = token.startsWith(`${INCOMING_CONTEXT_PREFIX}{{`);
  const braced = useIncomingContext ? token.slice(INCOMING_CONTEXT_PREFIX.length) : token;
  return { inner: braced.slice(2, -2), useIncomingContext };
};

/**
 * Builds a full attribute token string from an inner ctx string, optionally prefixing it with
 * `incomingContext` when the mapping should use transient context from the triggering request.
 */
const serializeAttr = (inner: string, useIncomingContext: boolean) =>
  `${useIncomingContext ? INCOMING_CONTEXT_PREFIX : ''}{{${inner}}}`;

const MAIN_CLASS = 'content-editable-area-for-context-attributes';
const CLASSES = {
  MAIN: MAIN_CLASS,
  LABEL: `${MAIN_CLASS}__label`,
  LABEL_WRAPPER: `${MAIN_CLASS}__label-wrapper`,
  ADD_ATTRIBUTE_BUTTON: `${MAIN_CLASS}__add-attribute-button`,
  CONTAINER: `${MAIN_CLASS}__container`,
  CTX_ATTRIBUTE: `${MAIN_CLASS}__ctx-attribute`,
  CTX_ATTRIBUTE_ERROR: `${MAIN_CLASS}__ctx-attribute--error`,
  CTX_ATTRIBUTE_ERROR_ICON: `${MAIN_CLASS}__ctx-attribute__error-icon`,
  CTX_ATTRIBUTE_SELECTED: `${MAIN_CLASS}__ctx-attribute--selected`,
  ERROR_MESSAGE: `${MAIN_CLASS}__error-message`,
  COUNTER: `${MAIN_CLASS}__counter`,
  COUNTER_TOOLTIP: `${MAIN_CLASS}__counter-tooltip`,
  FOOTER: `${MAIN_CLASS}__footer`,
};

/**
 * Calculates the character length of a string containing HTML, excluding any text
 * within '{{...}}' placeholders. It achieves this by rendering the HTML in a temporary,
 * non-visible element and then using its textContent to count characters.
 * @param text The HTML string to measure.
 * @returns The character count, excluding placeholder content.
 */
const getLengthWithoutBraces = (text: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = text;
  return (tempDiv.textContent || tempDiv.innerText || '').replace(/(?:incomingContext)?{{(.*?)}}/g, '').length;
};

/**
 * Converts the mixed content (text nodes and attribute spans) of the contentEditable
 * div back into a plain text string. Attribute spans are converted back to '{{ctxAttrString}}' format.
 * @param element The contentEditable div element.
 * @returns A plain text string representation of the editor's content.
 */
const getPlainText = (element: HTMLDivElement) => {
  let plainText = '';
  element.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      plainText += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.dataset.ctxAttrString) {
        plainText += serializeAttr(el.dataset.ctxAttrString, el.dataset.useIncomingContext === 'true');
      } else {
        plainText += el.textContent;
      }
    }
  });
  return plainText;
};

/**
 * Traverses the DOM of the contentEditable container to construct a plain text string
 * of the content from the beginning up to the start of a given Range (the cursor position).
 * This is used to accurately insert new attributes at the caret.
 * @param container The root node to traverse from (the contentEditable div).
 * @param range The selection range indicating the cursor position.
 * @returns A plain text string of the content before the cursor.
 */
const getPlainTextBeforeRange = (container: Node, range: Range): string => {
  let text = '';
  let ended = false;

  function recurse(node: Node) {
    if (ended) return;

    if (node === range.startContainer) {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent?.substring(0, range.startOffset) ?? '';
      }
      ended = true;
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.dataset && el.dataset.ctxAttrString) {
        text += serializeAttr(el.dataset.ctxAttrString, el.dataset.useIncomingContext === 'true');
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          recurse(node.childNodes[i]);
          if (ended) return;
        }
      }
    }
  }

  recurse(container);
  return text;
};

/**
 * Converts a selection range to plain text, including attribute placeholders.
 * Used for copy so clipboard contains {{ctx...}} text.
 * @param container The contentEditable div.
 * @param range The selection range.
 * @returns Plain text of the selection including attributes.
 */
const getPlainTextFromRange = (container: Node, range: Range): string => {
  const contents = range.cloneContents();
  let text = '';

  const processNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.dataset.ctxAttrString) {
        text += serializeAttr(el.dataset.ctxAttrString, el.dataset.useIncomingContext === 'true');
      } else {
        for (let i = 0; i < node.childNodes.length; i++) {
          processNode(node.childNodes[i]);
        }
      }
    }
  };

  for (let i = 0; i < contents.childNodes.length; i++) {
    processNode(contents.childNodes[i]);
  }
  return text;
};

/**
 * Replaces or removes a specific attribute at a given occurrence index using the same
 * splitting logic as the renderer to ensure consistency.
 * @param text The full text content.
 * @param index The 0-based index of the attribute to replace among all attributes.
 * @param newAttribute The new attribute string (including braces) or empty string to remove.
 * @returns The new text string.
 */
const replaceAttributeAtIndex = (text: string, index: number, newAttribute: string): string => {
  const regexSplit = buildAttrSplitRegex();
  const regexExact = buildAttrExactRegex();
  const parts = text.split(regexSplit).filter((p) => p);

  let count = 0;
  const newParts = parts.reduce((acc, part) => {
    if (regexExact.test(part)) {
      if (count === index) {
        if (newAttribute) {
          acc.push(newAttribute);
        }
      } else {
        acc.push(part);
      }
      count++;
    } else {
      acc.push(part);
    }
    return acc;
  }, [] as string[]);

  return newParts.join('');
};

/**
 * Retrieves the attribute string at a specific occurrence index.
 * @param text The full text content.
 * @param index The 0-based index of the attribute.
 * @returns The attribute string (e.g., "{{attr}}") or null if not found.
 */
const getAttributeAtIndex = (text: string, index: number): string | null => {
  const regexSplit = buildAttrSplitRegex();
  const regexExact = buildAttrExactRegex();
  const parts = text.split(regexSplit).filter((p) => p);

  let count = 0;
  for (const part of parts) {
    if (regexExact.test(part)) {
      if (count === index) {
        return part;
      }
      count++;
    }
  }
  return null;
};

const validateAttribute = (ctxAttrString: string, allAttributes: TContextAttribute[]) => {
  let error = '';
  let attributeProps;

  try {
    const { errorMessage, ...rest } = splitCtxString(ctxAttrString, allAttributes);
    error = errorMessage;
    attributeProps = rest;
  } catch (e) {
    if (e instanceof Error) error = e.message;
    else error = 'Action needed: Unknown error with context attribute';
  }

  return [error, attributeProps] as const;
};

const ContentEditableAreaForContextAttributes: React.FC<ContentEditableAreaForContextAttributesProps> = ({
  label,
  value,
  onChange,
  hasError,
  errorMessage,
  width,
  maxLength,
  onFocus,
  onBlur,
  hideCharCounter = false,
  className = '',
}) => {
  const { plugins }: { plugins: TContextPlugin[] } = usePlugins();
  const allAttributes = useMemo(() => plugins.flatMap((plugin) => Object.values(plugin.values)), [plugins]);
  const editorRef = useRef<HTMLDivElement>(null);
  const [selectedAttributeIndex, setSelectedAttributeIndex] = useState<number | null>(null);
  const [selection, setSelection] = useState<Range | null>(null);
  const [caretPosition, setCaretPosition] = useState<number | null>(null);

  const [attributeModalState, setAttributeModalState] = useState({
    isVisible: false,
    title: '',
    tagName: '',
    primaryButtonLabel: '',
    showRemoveAttributeButton: false,
    attributeId: '',
    attributeMapKey: '',
    attributeParameters: [] as TAttributeParameter[],
    attributeDefaultValue: '',
    useIncomingContext: false,
  });

  /**
   * Parses the plain text `value` prop (which may contain '{{...}}' placeholders)
   * and converts it into an array of Text nodes and styled `<span>` elements for attributes.
   * This is used to render the content within the contentEditable div. It also applies
   * special classes for selected attributes or attributes with errors.
   */
  const parseValueToHTML = useCallback(
    (text: string) => {
      const regexSplit = buildAttrSplitRegex();
      const regexExact = buildAttrExactRegex();
      let currentAttributeCount = 0;

      return text
        .split(regexSplit)
        .filter((part) => part)
        .map((part, idx, arr) => {
          if (regexExact.test(part)) {
            const prev = arr[idx - 1] || '';
            const next = arr[idx + 1] || '';
            if (prev.endsWith('{') || next.startsWith('}')) {
              return document.createTextNode(part);
            }
            const { inner: ctxAttrString, useIncomingContext } = parseAttrToken(part);
            // UI-level check for comma placement: only one comma, not at start/end, not in the middle of the attribute part
            let uiError = '';
            const commaIndex = ctxAttrString.indexOf(',');
            if (commaIndex > -1) {
              if (
                commaIndex === 0 ||
                commaIndex === ctxAttrString.length - 1 ||
                ctxAttrString.indexOf(',', commaIndex + 1) !== -1 ||
                ctxAttrString.slice(0, commaIndex).includes(',')
              ) {
                uiError = 'Invalid: Only one comma allowed, just before default value';
              }
            }
            const [errorMessage] = validateAttribute(ctxAttrString, allAttributes);
            const currentIndex = currentAttributeCount++;
            const isSelected = selectedAttributeIndex === currentIndex;
            const isError = !!errorMessage || !!uiError;
            const isErrorAndSelected = isError && isSelected;
            const span = document.createElement('span');
            span.className = `${CLASSES.CTX_ATTRIBUTE} ${
              isErrorAndSelected
                ? `${CLASSES.CTX_ATTRIBUTE_ERROR}--selected`
                : isError
                ? CLASSES.CTX_ATTRIBUTE_ERROR
                : isSelected
                ? CLASSES.CTX_ATTRIBUTE_SELECTED
                : ''
            }`;
            span.dataset.ctxAttrString = ctxAttrString;
            span.dataset.useIncomingContext = String(useIncomingContext);
            span.dataset.attrIndex = String(currentIndex);
            span.contentEditable = 'false';
            span.title = uiError ? uiError : isError ? errorMessage : 'Edit this attribute';
            span.textContent = part;
            return span;
          }
          return document.createTextNode(part);
        });
    },
    [selectedAttributeIndex, allAttributes],
  );

  const openEditModal = (ctxAttrString: string, useIncomingContext = false) => {
    const [, attributeProps] = validateAttribute(ctxAttrString, allAttributes);

    setAttributeModalState((state) => ({
      ...state,
      isVisible: true,
      title: 'Edit dynamic attribute',
      primaryButtonLabel: 'Update attribute',
      showRemoveAttributeButton: true,
      attributeId: attributeProps?.attributeId || '',
      attributeMapKey: attributeProps?.attributeMapKey || '',
      attributeParameters: attributeProps?.attributeParameters || [],
      attributeDefaultValue: attributeProps?.defaultValue || '',
      useIncomingContext,
    }));
  };

  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const selection = window.getSelection();

    if (target.dataset.attrIndex) {
      const idx = Number(target.dataset.attrIndex);
      const ctxAttrString = target.dataset.ctxAttrString || '';

      // Always create a range that selects the entire attribute span to track which specific attribute was clicked
      const range = document.createRange();
      range.selectNodeContents(target);
      setSelection(range);
      // Also update the browser selection to visually select the attribute
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }

      // If the attribute is already selected, open the modal for editing.
      if (selectedAttributeIndex === idx) {
        openEditModal(ctxAttrString, target.dataset.useIncomingContext === 'true');
      } else {
        // Otherwise, just select the attribute.
        setSelectedAttributeIndex(idx);
      }
    } else {
      // Deselect an attribute when the user clicks away from it
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        setSelection(range.cloneRange());
        const node = range.startContainer;
        const element = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
        if (element?.dataset?.attrIndex) {
          // Clicked inside an attribute, update selection state
          setSelectedAttributeIndex(Number(element.dataset.attrIndex));
        } else if (selectedAttributeIndex !== null) {
          // Clicked outside of a selected attribute, so deselect it.
          setSelectedAttributeIndex(null);
        }
      }
    }
  };

  const handleCopy = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !editorRef.current) return;
    const range = sel.getRangeAt(0);
    if (!editorRef.current.contains(range.commonAncestorContainer)) return;
    const plainText = getPlainTextFromRange(editorRef.current, range);
    e.clipboardData.setData('text/plain', plainText);
    e.preventDefault();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    const node = range.startContainer;

    // Handles deleting an attribute that is already selected.
    // If 'Backspace' or 'Delete' is pressed while an attribute is selected (highlighted),
    // it prevents the default browser action, calls a function to remove the attribute, and stops.
    if ((e.key === 'Backspace' || e.key === 'Delete') && selectedAttributeIndex !== null) {
      e.preventDefault();
      // Ensure selection Range is updated before removal
      setSelection(range.cloneRange());
      handleRemoveAttribute();
      return;
    }
    // Handles the case where the caret is just before an attribute and Delete is pressed
    if (e.key === 'Delete' && selectedAttributeIndex === null && range.collapsed) {
      // If caret is just before an attribute span, select it
      let nextNode = node.nodeType === Node.TEXT_NODE && node.nextSibling ? node.nextSibling : null;
      if (!nextNode && node.nodeType === Node.ELEMENT_NODE) {
        nextNode = node.childNodes[range.startOffset] || null;
      }
      if (nextNode && nextNode.nodeType === Node.ELEMENT_NODE && (nextNode as HTMLElement).dataset?.attrIndex) {
        e.preventDefault();
        const idx = Number((nextNode as HTMLElement).dataset?.attrIndex);
        // Create a range that selects the entire attribute span
        const attrRange = document.createRange();
        attrRange.selectNodeContents(nextNode);
        setSelection(attrRange);
        // Update browser selection to visually select the attribute
        sel.removeAllRanges();
        sel.addRange(attrRange);
        setSelectedAttributeIndex(idx);
        // After selection, the next Delete will remove it (handled above)
      }
    }

    // Handles opening the edit modal for a selected attribute.
    // If the 'Enter' key is pressed while an attribute is selected,
    // it prevents default behavior (like creating a new line) and opens the attribute editing modal.
    if (e.key === 'Enter' && selectedAttributeIndex !== null) {
      e.preventDefault();
      // Ensure selection Range is updated before opening modal
      setSelection(range.cloneRange());
      const rawAttr = getAttributeAtIndex(value, selectedAttributeIndex);
      if (rawAttr) {
        const { inner, useIncomingContext } = parseAttrToken(rawAttr);
        openEditModal(inner, useIncomingContext);
      }
      return;
    }

    // Handles the "first backspace" action, which selects an attribute instead of deleting a character.
    // It only triggers if 'Backspace' is pressed and the cursor is not highlighting a selection (range.collapsed is true).
    if (e.key === 'Backspace' && range.collapsed) {
      const element = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;

      // Handles the case where the cursor is inside an attribute's text content.
      // If the cursor is at the very end of the attribute's text, it prevents character deletion
      // and instead selects the attribute.
      if (element?.dataset?.attrIndex) {
        const textContentLength = element.textContent?.length ?? 0;
        if (range.startOffset === textContentLength) {
          e.preventDefault();
          // Create a range that selects the entire attribute span
          const attrRange = document.createRange();
          attrRange.selectNodeContents(element);
          setSelection(attrRange);
          // Update browser selection to visually select the attribute
          sel.removeAllRanges();
          sel.addRange(attrRange);
          setSelectedAttributeIndex(Number(element.dataset.attrIndex));
        }
        // Handles the case where the cursor is at the beginning of a text node that immediately follows an attribute.
        // Pressing backspace selects the preceding attribute.
      } else if (range.startOffset === 0 && node.previousSibling) {
        const prevSibling = node.previousSibling as HTMLElement;
        if (prevSibling?.dataset?.attrIndex) {
          e.preventDefault();
          // Create a range that selects the entire attribute span
          const attrRange = document.createRange();
          attrRange.selectNodeContents(prevSibling);
          setSelection(attrRange);
          // Update browser selection to visually select the attribute
          sel.removeAllRanges();
          sel.addRange(attrRange);
          setSelectedAttributeIndex(Number(prevSibling.dataset.attrIndex));
        }
        // Handles the case where the cursor is at the end of the contenteditable area,
        // and the last item is an attribute. It checks the node just before the cursor's position
        // inside the main container and selects it if it's an attribute.
      } else if (
        node.nodeType === Node.ELEMENT_NODE &&
        (node as HTMLElement).className.includes(CLASSES.CONTAINER) &&
        range.startOffset > 0
      ) {
        const child = node.childNodes[range.startOffset - 1] as HTMLElement;
        if (child?.dataset?.attrIndex) {
          e.preventDefault();
          // Create a range that selects the entire attribute span
          const attrRange = document.createRange();
          attrRange.selectNodeContents(child);
          setSelection(attrRange);
          // Update browser selection to visually select the attribute
          sel.removeAllRanges();
          sel.addRange(attrRange);
          setSelectedAttributeIndex(Number(child.dataset.attrIndex));
        }
      }
    }
  };

  const handleAddAttribute = () => {
    setSelectedAttributeIndex(null);
    setAttributeModalState({
      isVisible: true,
      title: 'Add dynamic attribute',
      tagName: '',
      primaryButtonLabel: 'Add attribute',
      showRemoveAttributeButton: false,
      attributeId: '',
      attributeMapKey: '',
      attributeParameters: [],
      attributeDefaultValue: '',
      useIncomingContext: false,
    });
  };

  const onClose = () => {
    setAttributeModalState({
      isVisible: false,
      title: '',
      tagName: '',
      primaryButtonLabel: '',
      showRemoveAttributeButton: false,
      attributeId: '',
      attributeMapKey: '',
      attributeParameters: [],
      attributeDefaultValue: '',
      useIncomingContext: false,
    });
    setSelectedAttributeIndex(null);
  };

  const handleApplyAttributeMapping = (
    _tagName: string,
    attributeId: string,
    parameters: TAttributeParameter[],
    defaultValue: string,
    selectedKey?: string,
    useIncomingContext?: boolean,
  ) => {
    const params = parameters?.map((p) => p.value).join('.');
    const inner = `${attributeId}${selectedKey ? `.${selectedKey}` : ''}${params ? `.${params}` : ''}${
      // Transient/incoming-context mappings never carry a default value
      useIncomingContext ? '' : defaultValue ? `,${defaultValue}` : ''
    }`;
    const newAttribute = serializeAttr(inner, !!useIncomingContext);
    let newText;

    if (selectedAttributeIndex !== null) {
      newText = replaceAttributeAtIndex(value, selectedAttributeIndex, newAttribute);
    } else {
      // Insert new attribute at caret position
      if (editorRef.current && selection) {
        const preCaretText = getPlainTextBeforeRange(editorRef.current, selection);
        const postCaretText = value.substring(preCaretText.length);

        newText = preCaretText + newAttribute + postCaretText;
      } else {
        newText = value + newAttribute;
      }
    }
    onChange(newText);
    onClose();
  };

  const handleRemoveAttribute = () => {
    if (selectedAttributeIndex !== null) {
      // Calculate position before removal to restore caret
      const regexSplit = buildAttrSplitRegex();
      const regexExact = buildAttrExactRegex();
      const parts = value.split(regexSplit);
      let currentPos = 0;
      let attrCount = 0;
      let foundPos = -1;

      for (const part of parts) {
        if (!part) continue;
        if (regexExact.test(part)) {
          if (attrCount === selectedAttributeIndex) {
            foundPos = currentPos;
            break;
          }
          attrCount++;
        }
        currentPos += part.length;
      }

      if (foundPos !== -1) {
        setCaretPosition(foundPos);
      }

      const newText = replaceAttributeAtIndex(value, selectedAttributeIndex, '');
      setSelectedAttributeIndex(null);
      onChange(newText);
      onClose();
    }
  };

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const sel = window.getSelection();
    const editor = editorRef.current;
    let caretPos: number | null = null;
    if (editor && sel?.rangeCount) {
      caretPos = getPlainTextBeforeRange(editor, sel.getRangeAt(0)).length;
    }
    const plain = getPlainText(e.currentTarget);
    onChange(plain);
    if (caretPos === null || !editor) return;
    const nodes = editor.childNodes;
    // Fix: If caret is at the end and the last node is a non-editable attribute span, place caret after the span
    if (caretPos === plain.length && nodes.length > 0) {
      const last = nodes[nodes.length - 1];
      if (last.nodeType === Node.ELEMENT_NODE && (last as HTMLElement).dataset.ctxAttrString) {
        setCaretPosition(plain.length);
        return;
      }
    }
    // If caret is after an attribute and a space, and user backspaced the space, keep caret after the attribute
    const before = plain.slice(0, caretPos);
    const regexEnd = new RegExp(`\\{\\{${CTX_ATTR_STRING_PATTERN}+}}$`);
    if (regexEnd.test(before)) {
      setCaretPosition(caretPos);
    }
  };

  /**
   * This effect synchronizes the DOM content of the editor with the `value` prop.
   * It compares the new nodes generated from `parseValueToHTML` with the existing
   * nodes in the editor and performs minimal updates (add, remove, replace) to
   * reflect the new state. This avoids a full re-render of the contentEditable div,
   * which would cause the user to lose their cursor position.
   */
  useEffect(() => {
    if (editorRef.current) {
      const newNodes = parseValueToHTML(value);
      const existingNodes = Array.from(editorRef.current.childNodes);

      let i = 0;
      let j = 0;
      while (i < newNodes.length || j < existingNodes.length) {
        const newNode = newNodes[i];
        const existingNode = existingNodes[j];

        if (!newNode) {
          // newNodes is shorter, remove remaining existing nodes
          editorRef.current.removeChild(existingNode);
          j++;
          continue;
        }

        if (!existingNode) {
          // newNodes is longer, add new nodes
          editorRef.current.appendChild(newNode);
          i++;
          continue;
        }

        if (newNode.nodeType !== existingNode.nodeType || newNode.textContent !== existingNode.textContent) {
          editorRef.current.replaceChild(newNode, existingNode);
        } else if (newNode.nodeType === Node.ELEMENT_NODE && existingNode.nodeType === Node.ELEMENT_NODE) {
          const newElement = newNode as HTMLElement;
          const existingElement = existingNode as HTMLElement;
          if (newElement.className !== existingElement.className) {
            existingElement.className = newElement.className;
          }
        }

        i++;
        j++;
      }
    }
  }, [value, parseValueToHTML]);

  /**
   * This effect programmatically restores the caret (cursor) position within the editor.
   * It runs whenever the `caretPosition` state is updated, which typically happens
   * after an operation (like deleting or typing an attribute) that causes a re-render and
   * would otherwise lead to the loss of the cursor's position.
   *
   * The effect works by traversing the child nodes of the editor, counting characters
   * to find the specific text node and offset that corresponds to the `caretPosition`
   * value. If the caret is inside or at the edge of an attribute span, it positions
   * the cursor immediately after that span. It then creates a new selection Range and
   * applies it to restore the caret. Finally, it resets `caretPosition` to `null` to
   * prevent re-running on subsequent renders.
   */
  useEffect(() => {
    if (editorRef.current && caretPosition !== null) {
      const sel = window.getSelection();
      if (sel) {
        let charCount = 0;
        let targetNode: Node | null = null;
        let targetOffset = 0;

        const findNodeAndOffset = (node: Node) => {
          if (targetNode) return;

          if (node.nodeType === Node.TEXT_NODE) {
            const textLength = node.textContent?.length ?? 0;
            if (charCount + textLength >= caretPosition) {
              targetNode = node;
              targetOffset = caretPosition - charCount;
            } else {
              charCount += textLength;
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            if (el.dataset.ctxAttrString) {
              const textLength = el.textContent?.length ?? 0;
              if (charCount + textLength >= caretPosition) {
                // Position cursor after the attribute span
                if (el.parentNode) {
                  targetNode = el.parentNode;
                  targetOffset = Array.from(targetNode.childNodes).indexOf(el) + 1;
                }
              } else {
                charCount += textLength;
              }
            } else {
              node.childNodes.forEach(findNodeAndOffset);
            }
          }
        };

        findNodeAndOffset(editorRef.current);

        if (targetNode) {
          const range = document.createRange();
          range.setStart(targetNode, targetOffset);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
      setCaretPosition(null); // Reset after setting position
    }
  }, [caretPosition]);

  /**
   * This effect tracks the user's cursor position (selection) within the document.
   * It adds a 'selectionchange' event listener to update the component's `selection`
   * state whenever the user clicks or moves the caret. It also handles deselecting a
   * selected attribute if the user's selection moves outside of it.
   */
  useEffect(() => {
    const handleSelectionChange = () => {
      // Issue: Updating an attribute after selecting it with the backspace and enter keys would incorrectly insert the new attribute instead of replacing the existing one.
      // The problem was that the selectedAttributeId was being reset when the attribute editing modal opened.

      // Fix: Prevent the selection change handler from running while the modal is visible, which resolves the issue.
      if (attributeModalState.isVisible) {
        return;
      }

      const currentSelection = window.getSelection();
      if (
        currentSelection &&
        currentSelection.rangeCount > 0 &&
        editorRef.current?.contains(currentSelection.anchorNode)
      ) {
        const range = currentSelection.getRangeAt(0);

        if (
          !selection ||
          range.startContainer !== selection.startContainer ||
          range.startOffset !== selection.startOffset ||
          range.endContainer !== selection.endContainer ||
          range.endOffset !== selection.endOffset
        ) {
          setSelection(range.cloneRange());
        }

        if (selectedAttributeIndex !== null) {
          const node = range.startContainer;
          const element = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
          const idx = element?.dataset?.attrIndex ? Number(element.dataset.attrIndex) : null;

          if (idx !== selectedAttributeIndex) {
            setSelectedAttributeIndex(null);
          }
        }
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [selectedAttributeIndex, selection, attributeModalState.isVisible]);

  const {
    isVisible,
    title,
    tagName,
    primaryButtonLabel,
    showRemoveAttributeButton,
    attributeId,
    attributeMapKey,
    attributeParameters,
    attributeDefaultValue,
    useIncomingContext,
  } = attributeModalState;

  return (
    <>
      <div className={`${CLASSES.MAIN} ${className}`} style={{ width }}>
        <header className={CLASSES.LABEL_WRAPPER}>
          <label className={CLASSES.LABEL}>{label}</label>
          <div className={CLASSES.ADD_ATTRIBUTE_BUTTON}>
            <FlightButton
              ariaLabel={'add dynamic attribute'}
              label="{+} Add attribute"
              theme="link"
              onClick={handleAddAttribute}
            />
          </div>
        </header>
        <div
          ref={editorRef}
          className={CLASSES.CONTAINER}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onClick={handleEditorClick}
          onKeyDown={handleKeyDown}
          onCopy={handleCopy}
          role="textbox"
          tabIndex={0}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <div className={CLASSES.FOOTER}>
          {hasError && <span className={CLASSES.ERROR_MESSAGE}>{errorMessage}</span>}
          {!hideCharCounter && !!maxLength && (
            <>
              <span className={CLASSES.COUNTER}>
                {getLengthWithoutBraces(value)} / {maxLength}
              </span>
              {/{{(.*?)}}/.test(value) && (
                <span className={CLASSES.COUNTER_TOOLTIP}>
                  <FlightTooltip
                    description="This count does not include text from added attributes. The final message length can vary by user."
                    direction="left"
                  >
                    {getIcon('exclamationCircle', {})}
                  </FlightTooltip>
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <AttributeMappingModal
        isVisible={isVisible}
        onClose={onClose}
        onApply={handleApplyAttributeMapping}
        tagName={tagName}
        attributeId={attributeId}
        attributeMapKey={attributeMapKey}
        attributeParameters={attributeParameters}
        attributeDefaultValue={attributeDefaultValue}
        useIncomingContext={useIncomingContext}
        showRemoveAttributeButton={showRemoveAttributeButton}
        onRemoveAttribute={handleRemoveAttribute}
        title={title}
        primaryButtonLabel={primaryButtonLabel}
      />
    </>
  );
};

export default ContentEditableAreaForContextAttributes;
